import { AgGridChoiceFilterAdapter, AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { TitleWithTooltipColumns } from 'components/TitleWithTooltipColumns';
import { CheckboxWithToolTip } from 'components/WithTooltip';
import { UerChapter, UerClassifier, UerStatus, WorkInSumTitles } from 'constantsProject';
import { useRequest } from 'features/request/useRequest';
import type {
    ChapterEnum,
    PriceListUerRowData,
    PriceListUerRowDataFilterInput,
    UerClassifierEnum,
    UerRecordStatusEnum,
    WorkInSumEnum,
} from 'gql/graphql';
import { ContractChangeStatusEnum } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { getDefaultFilterCleaner, usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';
import { usePin } from 'hooks/usePin';
import { useProjectData } from 'hooks/useProjectData';
import { useSelection } from 'hooks/useSelection';
import { Button, Checkbox, cnMixSpace, IconExpand } from 'uikit';
import { destroyAgGridTableFilters, getCheckedIdsArr } from 'utils';
import { createWhereVariablesGetter } from 'utils/getWhereVariables';
import { getYesNoStatus } from 'utils/getYesNoTableStatus';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import Style from '../AgreementPriceListUer/AgreementPriceListUer.module.css';
import {
    getAgreementPriceListUer,
    getAgreementUerChapterFilterItems,
    getAgreementUerCodeFilterItems,
    getAgreementUerJustificationFilterItems,
    getAgreementUerStatusFilterItems,
    getAgreementUerTypeFilterItems,
    getContractChangeUerGroupFullNameFilter,
    getContractChangeUerNameFilter,
    getContractChangeUerSectionNameFilter,
    getContractChangeUerWorkTypeInSumFilter,
    getContractChangeUerWorkTypeNameFilter,
} from '../AgreementsQueries';

import { useUERExtraColumns } from './useExtraUERColumns';

const GetTooltipHeader = (title: string, childTooltip: string) => () =>
    TitleWithTooltipColumns({
        title,
        childTooltip,
        style: {
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
        },
    });

const filters = [
    'sectionName',
    'groupName',
    'code',
    'name',
    'chapter',
    'uerClassifier',
    'status',
    'justification',
    'workKind.workName',
    'workKind.workInSum',
];

const getWhereVariables = createWhereVariablesGetter<PriceListUerRowDataFilterInput | undefined>({
    searchStringFields: [],
    filtersToWhere: (selectedFilters: [string, boolean[] | string[]][]) =>
        selectedFilters.map(([fieldName, value]) => {
            if (
                fieldName === 'isIncludedInWorkOrder' ||
                fieldName === 'isIncludedInAct' ||
                fieldName === 'isIncludedInWorkOrderChange'
            ) {
                return {
                    [fieldName]: {
                        eq: value[0] === 'Да',
                    },
                };
            }

            if (fieldName === 'groupName') {
                return {
                    groupFullName: {
                        in: value,
                    },
                };
            }

            if (['workKind.workName', 'workKind.workInSum'].includes(fieldName)) {
                return {
                    workKind: {
                        [fieldName.includes('workName') ? 'workName' : 'workInSum']: { in: value },
                    },
                };
            }

            return {
                [fieldName]: {
                    in: value,
                },
            };
        }),
});

export const useGetAgreementUerItems = ({
    contractChangeId,
    refreshData,
    disabledByStatus,
    isDisabledByProjectStatus,
    needUpdateCurrentPriceList,
    contractChangeStatus,
    needUpdatePriceListHandler,
    handleExpandRow,
}: {
    contractChangeId: number;
    refreshData: () => void;
    disabledByStatus: boolean;
    isDisabledByProjectStatus: boolean;
    needUpdateCurrentPriceList?: boolean;
    contractChangeStatus?: ContractChangeStatusEnum;
    needUpdatePriceListHandler?: (nextState: boolean) => void;
    handleExpandRow: (data: PriceListUerRowData) => void;
}) => {
    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId, false);
    const tableRef = useRef<AgGridReact>();

    const [queryWhere, setQueryWhere] = useState<any>(undefined);
    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});

    const checkedIdsArr = useMemo(() => getCheckedIdsArr(checkedIds), [checkedIds]);

    const filterChangeHandler = useCallback(
        (value: { checkedValues: string[]; fieldName: string; fullDataSet: { data: { name: string } }[] }) => {
            setQueryWhere((prevFilters: any) => ({
                ...prevFilters,
                [value.fieldName]: value.checkedValues,
            }));
        },
        [],
    );

    usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(
                    filterChangeHandler as any,
                    `agreementUerItems`,
                    getDefaultFilterCleaner({
                        changeFilterHandler: filterChangeHandler as any,
                        setFiltersWhere: setQueryWhere,
                    }),
                ),
            }),
            [filterChangeHandler],
        ),
    );

    const variables = useMemo(
        () => ({
            contractChangeId,
            where: getWhereVariables(queryWhere),
        }),
        [contractChangeId, queryWhere],
    );

    const [agreementUerData, agreementUerStatus, , agreementUerRequest] = useRequest({
        requestId: 'agreementUer',
        query: getAgreementPriceListUer,
        variables,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'agreementUer',
        requestFn: agreementUerRequest,
        requestVariables: variables,
    });

    useEffect(() => {
        if (getRowParams?.current) {
            getRowParams?.current?.successCallback(
                agreementUerData?.uerContractChangePriceListUer?.items ?? [],
                agreementUerData?.uerContractChangePriceListUer?.totalCount,
            );
        }
    }, [
        getRowParams,
        agreementUerData?.uerContractChangePriceListUer?.items,
        agreementUerData?.uerContractChangePriceListUer?.totalCount,
    ]);

    useEffect(() => {
        if (needUpdateCurrentPriceList && needUpdatePriceListHandler) {
            tableRef?.current?.api?.refreshInfiniteCache();
            needUpdatePriceListHandler(false);
        }
    }, [needUpdateCurrentPriceList, needUpdatePriceListHandler, variables]);

    useEffect(() => {
        if (contractChangeStatus !== ContractChangeStatusEnum.Formation) {
            setCheckedIds({});
        }
    }, [contractChangeStatus]);

    const refreshAgreementUer = useCallback(
        (skipCheckedReset?: boolean) => {
            tableRef?.current?.api?.refreshInfiniteCache();
            refreshData();

            if (!skipCheckedReset) {
                setCheckedIds({});
            }
        },
        [refreshData],
    );

    const [selection, , , setSelection] = useSelection('useGetAgreementUerItems');
    const [pinnedItems, , , setPinned] = usePin('useGetAgreementUerItems');

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const { otherCostTableExtColumns } = useUERExtraColumns(projectData?.contractId);

    const changeCheckboxHandler = useCallback(
        (rowId: number) =>
            ({ checked }: { checked: boolean }) => {
                setCheckedIds((ids) => ({ ...ids, [rowId]: checked }));
            },
        [],
    );

    const columns = useMemo<(ColDef<PriceListUerRowData> | ColGroupDef<PriceListUerRowData>)[]>(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                width: 74,
                resizable: false,
                lockPosition: 'left',
                pinned: true,
                cellRenderer: ({ data }: { data: PriceListUerRowData }) => {
                    if (!data) {
                        return '';
                    }

                    return (
                        <div className={Style.wrapperMenu}>
                            {disabledByStatus ? (
                                <CheckboxWithToolTip
                                    checkboxProps={{ checked: false }}
                                    tooltipProps={{
                                        mode: 'mouseover',
                                        content: 'Удаление возможно только в статусе "На формировании"',
                                    }}
                                    fakeDisabled
                                />
                            ) : (
                                <Checkbox
                                    checked={checkedIds[data.id]}
                                    onChange={changeCheckboxHandler(data.id)}
                                    className={cnMixSpace({ mT: '3xs' })}
                                    disabled={isDisabledByProjectStatus}
                                />
                            )}
                            <Button
                                onClick={() => handleExpandRow(data)}
                                iconLeft={IconExpand}
                                onlyIcon
                                view="clear"
                                size="xs"
                            />
                        </div>
                    );
                },
            },
            {
                field: 'sectionName',
                headerName: 'Раздел',
                width: 140,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeUerSectionNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Раздел',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getContractChangeUerSectionNameFilter,
                },
            },
            {
                field: 'groupName',
                headerName: 'Подраздел',
                width: 140,
                valueFormatter: (params) => {
                    const { data } = params;

                    return data ? `${data?.groupCode} ${data?.groupName}` : '';
                },
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeUerGroupFullNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Подраздел',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getContractChangeUerGroupFullNameFilter,
                },
            },
            {
                headerName: 'Шифр',
                field: 'code',
                width: 200,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeUerCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getAgreementUerCodeFilterItems,
                },
            },
            {
                field: 'name',
                headerName: 'Наименование',
                flex: 1,
                minWidth: 300,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeUerNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getContractChangeUerNameFilter,
                },
            },
            {
                field: 'unit',
                headerName: 'Ед.изм.',
                width: 120,
            },
            {
                field: 'chapter',
                headerName: 'Глава в расчете',
                width: 115,
                valueFormatter: ({ data }) => (data?.chapter ? UerChapter[data?.chapter] : ''),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeUerChapterFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Глава в расчете',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: ChapterEnum | null }) =>
                                value ? UerChapter[value] : '<Нет значения>',
                        },
                    ],
                    query: getAgreementUerChapterFilterItems,
                },
            },
            {
                field: 'uerClassifier',
                headerName: 'Тип УЕР',
                width: 200,
                valueFormatter: ({ data }) => (data?.uerClassifier ? UerClassifier[data?.uerClassifier] : ''),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeUerTypeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Тип УЕР',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: UerClassifierEnum | null }) =>
                                value ? UerClassifier[value] : '<Нет значения>',
                        },
                    ],
                    query: getAgreementUerTypeFilterItems,
                },
            },
            ...otherCostTableExtColumns,
            {
                field: 'status',
                headerName: 'Статус записи',
                width: 130,
                valueFormatter: ({ data }) => (data?.status ? UerStatus[data?.status] : ''),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeUerStatusFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Статус записи',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: UerRecordStatusEnum | null }) =>
                                value ? UerStatus[value] : '<Нет значения>',
                        },
                    ],
                    query: getAgreementUerStatusFilterItems,
                },
            },
            {
                headerName: 'Используется',
                marryChildren: true,
                children: [
                    {
                        field: 'isIncludedInWorkOrder',
                        headerName: 'НЗ',
                        width: 100,
                        valueFormatter: ({ data }) => getYesNoStatus(data?.isIncludedInWorkOrder),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: filterChangeHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInAct',
                        headerName: 'Акт',
                        width: 100,
                        valueFormatter: ({ data }) => getYesNoStatus(data?.isIncludedInAct),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: filterChangeHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInWorkOrderChange',
                        headerName: 'Изм. к НЗ',
                        width: 100,
                        valueFormatter: ({ data }) => getYesNoStatus(data?.isIncludedInWorkOrderChange),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: filterChangeHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                ],
            },
            {
                headerName: 'Виды работ',
                marryChildren: true,
                children: [
                    {
                        field: 'workKind.workCode',
                        headerName: 'Шифр',
                        width: 136,
                    },
                    {
                        field: 'workKind.workName',
                        headerName: 'Наименование',
                        width: 170,
                        filter: AgGridCustomFilterAdapter,
                        filterParams: {
                            hideTableHeader: true,
                            variables: { contractChangeId },
                            changeFilterHandler: filterChangeHandler,
                            dataAccessor: 'uerContractChangeUerWorkTypeNameFilter',
                            whereVariables: ['value'],
                            childFieldAccessor: 'value',
                            columns: [
                                {
                                    headerName: 'Наименование',
                                    field: 'value',
                                    checkboxSelection: true,
                                    width: 400,
                                },
                            ],
                            query: getContractChangeUerWorkTypeNameFilter,
                        },
                    },
                    {
                        field: 'workKind.workUnit',
                        headerName: 'Ед.изм.',
                        width: 100,
                    },
                    {
                        field: 'workKind.workInSum',
                        headerComponent: GetTooltipHeader(
                            'Участие в суммировании ФО по виду работ',
                            'Участие в суммировании ФО по виду работ',
                        ),
                        width: 162,
                        valueFormatter: ({ value }: { value: WorkInSumEnum | null }) =>
                            value ? WorkInSumTitles[value] : '',
                        filter: AgGridCustomFilterAdapter,
                        filterParams: {
                            hideTableHeader: true,
                            variables: { contractChangeId },
                            changeFilterHandler: filterChangeHandler,
                            dataAccessor: 'uerContractChangeUerWorkTypeInSumFilter',
                            whereVariables: ['value'],
                            childFieldAccessor: 'value',
                            columns: [
                                {
                                    headerName: 'Участие в суммировании ФО по виду работ',
                                    field: 'value',
                                    checkboxSelection: true,
                                    width: 400,
                                    valueFormatter: ({ value }: { value: WorkInSumEnum }) => WorkInSumTitles[value],
                                },
                            ],
                            query: getContractChangeUerWorkTypeInSumFilter,
                        },
                    },
                    {
                        field: 'workKind.workRatio',
                        headerName: 'К перехода',
                        width: 136,
                    },
                ],
            },
            {
                field: 'justification',
                headerName: 'Обоснование',
                width: 160,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeUerJustificationFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Обоснование',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: UerRecordStatusEnum | null }) =>
                                value ? UerStatus[value] : '<Нет значения>',
                        },
                    ],
                    query: getAgreementUerJustificationFilterItems,
                },
            },
        ],
        [
            changeCheckboxHandler,
            checkedIds,
            contractChangeId,
            disabledByStatus,
            filterChangeHandler,
            handleExpandRow,
            isDisabledByProjectStatus,
            otherCostTableExtColumns,
        ],
    );

    const visibleColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    const clearCheckedHandler = useCallback(() => {
        setCheckedIds({});
    }, []);

    const columnsForTableSettings = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 6) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const handleTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: columnsForTableSettings,
                incomingColumns: visible,
                setPinned,
                setSelection,
            });
        },
        [columnsForTableSettings, setPinned, setSelection],
    );

    const totalItems = useMemo(
        () => agreementUerData?.uerContractChangePriceListUer?.totalCount ?? 0,
        [agreementUerData?.uerContractChangePriceListUer?.totalCount],
    );

    const fetchedItems = useMemo(() => {
        if (!getRowParams?.current?.endRow || !totalItems) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalItems) {
            return totalItems;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [totalItems, getRowParams?.current?.endRow]);

    const refreshFilters = useCallback(() => {
        if (tableRef) {
            destroyAgGridTableFilters({
                api: tableRef.current?.api,
                columns: filters,
            });
        }
    }, []);

    return {
        rowCount: fetchedItems,
        totalCount: totalItems,
        columns: visibleColumns,
        columnsForTableSettings,
        isColumnVisible,
        datasource: dataSource,
        requestStatus: agreementUerStatus,
        handleTableSettingsConfirm,
        filterChangeHandler,
        refreshAgreementUer,
        checkedIds: checkedIdsArr,
        clearCheckedHandler,
        tableRef,
        refreshFilters,
    };
};
